import React, { useEffect, useRef, useState } from 'react';

import {
  FiltroTipoDeDano,
  FiltrosOficina,
  SituacaoAtendimento,
  TIPOS_DE_DANO,
} from '../../../api/dashboardOficinaApi';
import '../../../components/modal/contac/filtro/ModalFiltro.css';
import '../../../shared/ui/dashboard-base.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Filtros do Dashboard Oficina (at-86).
 *
 * ⚠️ MONTAGEM CONDICIONAL, não prop `isOpen`: quem renderiza faz `{aberto && <Modal/>}`. Com o
 * modal montado o tempo todo, o rascunho descartado no "Cancelar" sobrevive para a abertura
 * seguinte (medido na at-61). Assim, o `useState` inicial é a única fonte do estado e o caso
 * deixa de existir por construção.
 *
 * ⚠️ Recebe `filtrosAplicados` porque a tela NÃO mostra resumo dos filtros fora daqui: quem
 * reabre para conferir precisa ver o que está valendo, senão lê "nenhum filtro" numa tela
 * filtrada e um "Aplicar" logo em seguida limpa tudo em silêncio (BUG-V43).
 */

const SITUACOES: Array<{ valor: SituacaoAtendimento; rotulo: string; ajuda: string }> = [
  { valor: 'ambos', rotulo: 'Os dois', ajuda: 'Veículos na oficina e já entregues' },
  { valor: 'na-oficina', rotulo: 'Na oficina', ajuda: 'Somente atendimentos em andamento' },
  { valor: 'entregues', rotulo: 'Entregues', ajuda: 'Somente atendimentos já encerrados' },
];

interface Props {
  filtrosAplicados: FiltrosOficina;
  esteiras: Array<{ id: string; nome: string }>;
  onAplicar: (filtros: FiltrosOficina) => void;
  onFechar: () => void;
  sidebarRef?: React.RefObject<HTMLElement>;
}

const OficinaFiltroModal: React.FC<Props> = ({
  filtrosAplicados,
  esteiras,
  onAplicar,
  onFechar,
  sidebarRef,
}) => {
  const [situacao, setSituacao] = useState<SituacaoAtendimento>(filtrosAplicados.situacao ?? 'ambos');
  const [dataInicio, setDataInicio] = useState(filtrosAplicados.dataInicio ?? '');
  const [dataFim, setDataFim] = useState(filtrosAplicados.dataFim ?? '');
  const [esteiraId, setEsteiraId] = useState(filtrosAplicados.esteiraId ?? '');
  const [carClass, setCarClass] = useState(filtrosAplicados.carClass ?? '');
  const [damageType, setDamageType] = useState<FiltroTipoDeDano | ''>(
    filtrosAplicados.damageType ?? '',
  );
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const aoClicarFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      if (ref.current && !ref.current.contains(alvo) && !sidebarRef?.current?.contains(alvo)) {
        onFechar();
      }
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar, sidebarRef]);

  const aplicar = () => {
    onAplicar({
      situacao,
      dataInicio: dataInicio || undefined,
      dataFim: dataFim || undefined,
      esteiraId: esteiraId || undefined,
      carClass: carClass || undefined,
      damageType: damageType || undefined,
    });
  };

  const limpar = () => {
    setSituacao('ambos');
    setDataInicio('');
    setDataFim('');
    setEsteiraId('');
    setCarClass('');
    setDamageType('');
  };

  /*
   * ⚠️ A ESTRUTURA É A DO MODAL DE FILTROS DO CONTAC, classe por classe:
   * `modal-filtro-overlay` > `modal-filtro-content` > `filtro-modal-card` > header/body/footer.
   *
   * A primeira versão inventou nomes parecidos (`filtro-modal-overlay`, `filtro-modal`) que não
   * existem em CSS nenhum — o modal abria sem posicionamento e sem fundo, solto no fim da página,
   * e parecia que o botão não fazia nada. Passava no teste porque o jsdom não aplica CSS: só a
   * tela denuncia. Mesma classe de defeito do `<p className="error">` e do `config-help-btn`.
   */
  return (
    <div className="modal-filtro-overlay">
      <div className="modal-filtro-content" ref={ref} role="dialog" aria-modal="true" aria-label="Filtros">
        <div className="filtro-modal-card do-filtro-card">
          <div className="filtro-modal-header">
            <div>
              <h2 className="filtro-modal-title">Filtros</h2>
              <p className="filtro-modal-subtitle">
                O recorte vale para todas as abas do painel
              </p>
            </div>
            <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
              &times;
            </button>
          </div>

          <div className="filtro-modal-body">
          <section className="filtro-section">
            <h3>Situação do atendimento</h3>
            <p className="admin-hint">
              Vale para todas as abas: define quais veículos entram na conta.
            </p>
            <div className="filtro-grid-2">
              {SITUACOES.map((opcao) => (
                <label key={opcao.valor} style={{ display: 'block', marginBottom: 8 }}>
                  <input
                    type="radio"
                    name="situacao"
                    value={opcao.valor}
                    checked={situacao === opcao.valor}
                    onChange={() => setSituacao(opcao.valor)}
                  />{' '}
                  <strong>{opcao.rotulo}</strong>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--tema-texto-3, #6b6a66)', marginLeft: 22 }}>
                    {opcao.ajuda}
                  </span>
                </label>
              ))}
            </div>
          </section>

          <section className="filtro-section">
            <h3>Período de entrada</h3>
            <p className="admin-hint">
              Filtra pela data em que o veículo entrou na oficina.
            </p>
            <div className="filtro-grid-2">
              <div>
                <label htmlFor="of-data-inicio">De</label>
                <input
                  id="of-data-inicio"
                  type="date"
                  className="premium-text-input"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="of-data-fim">Até</label>
                <input
                  id="of-data-fim"
                  type="date"
                  className="premium-text-input"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                />
              </div>
            </div>
          </section>

          <section className="filtro-section">
            <h3>Recorte</h3>
            <div className="filtro-grid-2">
              <div>
                <label htmlFor="of-esteira">Esteira</label>
                <div className="select-wrapper">
                  <select
                    id="of-esteira"
                    className="premium-text-input"
                    value={esteiraId}
                    onChange={(e) => setEsteiraId(e.target.value)}
                    disabled={esteiras.length === 0}
                  >
                    <option value="">Todas</option>
                    {esteiras.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="of-classe">Classe do veículo</label>
                <div className="select-wrapper">
                  <select
                    id="of-classe"
                    className="premium-text-input"
                    value={carClass}
                    onChange={(e) => setCarClass(e.target.value)}
                  >
                    <option value="">Todas</option>
                    <option value="ASSOCIADO">Associado</option>
                    <option value="TERCEIRO">Terceiro</option>
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="of-dano">Tipo de dano</label>
                <div className="select-wrapper">
                  <select
                    id="of-dano"
                    className="premium-text-input"
                    value={damageType}
                    onChange={(e) => setDamageType(e.target.value as FiltroTipoDeDano | '')}
                  >
                    <option value="">Todos</option>
                    {TIPOS_DE_DANO.map((tipo) => (
                      <option key={tipo.valor} value={tipo.valor}>
                        {tipo.rotulo}
                      </option>
                    ))}
                  </select>
                </div>
                {/*
                  O campo é opcional no cadastro e boa parte dos atendimentos está sem ele. Sem
                  esta frase, "Não informado" parece um tamanho de dano, e o usuário não entende
                  por que a soma dos três tamanhos não fecha com o total da tela.
                */}
                <p className="do-hint-campo">
                  O tipo de dano é opcional no cadastro do veículo. Use <strong>Não informado</strong>{' '}
                  para ver os atendimentos em que ele ficou em branco.
                </p>
              </div>
            </div>
            </section>
          </div>

          <div className="filtro-modal-footer">
            <button type="button" className="filtro-reset-btn" onClick={limpar}>
              Limpar filtros
            </button>
            <button type="button" className="filtro-btn-secondary" onClick={onFechar}>
              Cancelar
            </button>
            <button type="button" className="filtro-btn-primary" onClick={aplicar}>
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OficinaFiltroModal;
